import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
} from "@material-ui/core";
import { Visibility, Edit, Delete } from "@material-ui/icons";
import EnhancedTableHead from "./EnhancedTableHead";
import DeleteModal from "./deleteModal/DeleteModal";
import getDateFromString from "../../utils/getDateFromString";
import { makeStyles } from "@material-ui/core/styles";
import "./investigation.css"; // Keep if you have additional overrides
import { useDispatch } from "react-redux";
import { getUserById } from "../../store/action/user";
import { getClaimDetails } from "../../store/action/claim";

// Below is an example makeStyles, modeled after the Sinistres styling:
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    borderRadius: "11px",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.06)",
  },
  tableWrapper: {
    padding: "40px",
  },
  tableHeadRow: {
    height: "48px",
    background: "rgba(243, 246, 249, 1)",
    borderRadius: "6px",
  },
  tableCellSmall: {
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "21px",
    color: "rgba(70, 78, 95, 1)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  titrePage: {
    fontFamily: "Poppins",
    fontSize: "13.8px",
    fontWeight: 600,
    lineHeight: "20.7px",
    color: "rgba(63, 66, 84, 1)",
  },
  pagination: {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    color: "rgba(32, 18, 70, 1)",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center", // Center the buttons horizontally
    alignItems: "center",    // Center the buttons vertically
    gap: "10px",             // Add spacing between buttons
  },
  
  viewBtn: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: "rgba(229, 242, 255, 1)",
    border: "none",
    lineHeight: "18px",
    color: "rgba(86, 157, 210, 1)",
  },
  
  editBtn: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: "rgba(229, 255, 242, 1)",
    border: "none",
    lineHeight: "18px",
    color: "rgba(50, 168, 124, 1)",
  },
  
  deleteBtn: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: "rgba(255, 229, 229, 1)",
    border: "none",
    lineHeight: "18px",
    color: "rgba(168, 50, 50, 1)",
  },
  titre: {
    fontFamily: "Poppins",
    fontSize: "13.8px",
    fontWeight: 600,
    lineHeight: "20.7px",
    color: "rgba(63, 66, 84, 1)",
  }
}));

const headRows = [
  { id: "claimNumber", align: "left", label: "N° Sinistre" },
  { id: "Titre", align: "left", label: "Titre" },
  { id: "userID", align: "left", label: "Responsable" },
  { id: "assigne", align: "left", label: "Assigné à" },
  { id: "created", align: "left", label: "Date de création" },
  { id: "updatedDate", align: "left", label: "Dernière mise à jour" },
  { id: "state", align: "left", label: "Statut actuel" },
  { id: "actionsID", align: "left", label: "Actions" },
];

const InvestigationTable = ({
  investigationData,
  loading,
  order,
  orderBy,
  page,
  rowsPerPage,
  handleRequestSort,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
  handleChangePage,
  handleChangeRowsPerPage,
  open,
  handleClose,
  selectedId,
  handleDeleteInvestigation,
  handleAddInvestigation,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [users, setUsers] = useState({});
  const [claims, setClaims] = useState({});

  useEffect(() => {
    // Fetch user data (responsables/assigne) if needed
    const fetchUsers = async () => {
      const userIds = [
        ...new Set([
          ...investigationData.map((row) => row.userID),
          ...investigationData.map((row) => row.assigne),
        ]),
      ];

      const userPromises = userIds.map((id) => getUserById(id));
      const userResults = await Promise.allSettled(userPromises);
      const fetchedUsers = userResults.reduce((acc, result, index) => {
        if (result.status === "fulfilled") {
          acc[userIds[index]] = result.value;
        }
        return acc;
      }, {});
      setUsers(fetchedUsers);
    };

    const fetchClaims = async () => {
      const claimIds = [
        ...new Set(investigationData.map((row) => row.sinistreID)),
      ];
      const claimPromises = claimIds.map((id) => getClaimDetails(id));
      const claimResults = await Promise.allSettled(claimPromises);
      const fetchedClaims = claimResults.reduce((acc, result, index) => {
        if (result.status === "fulfilled") {
          acc[claimIds[index]] = result.value;
        }
        return acc;
      }, {});
      setClaims(fetchedClaims);
    };

    fetchUsers();
    fetchClaims();
  }, [investigationData]);

  if (loading) {
    return <div style={{ padding: "20px" }}>Loading...</div>;
  }

  return (
    <>
      {/* Page Title and (Optional) Add Button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginTop: "5px",
          marginBottom: "15px",
        }}
      >
        <div className="titrePage">Liste des investigations</div>
        {/* 
        If you want an "Add" button:
          <div style={{ marginLeft: "auto" }}>
            <Button
              style={{
                width: "100px",
                height: "34px",
                borderRadius: "6px",
                background: "#D5E5F2",
                border: "none",
                fontFamily: "Poppins",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "18px",
                color: "#4969B4",
                marginBottom: "5px",
              }}
              onClick={handleAddInvestigation}
            >
              Ajouter +
            </Button>
          </div>
        */}
      </div>

      <TableContainer component={Paper} className={classes.root}>
        <div className={classes.tableWrapper}>
          <Table>
            <TableHead>
              <EnhancedTableHead
                headRows={headRows}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? investigationData.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : investigationData
              ).map((row) => (
                <TableRow key={row.idInvestigation}>
                  <TableCell className={classes.tableCellSmall}>
                    {row.claimNumber}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {row.titre}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {users[row.userID]?.firstName || row.userID}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {users[row.assigne]?.firstName || row.assigne}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {getDateFromString(row.createdAt?.toString())}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {getDateFromString(row.updatedAt?.toString())}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {row.state}
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
  <div className={classes.buttonContainer}>
    <Tooltip title="Voir" arrow>
      <IconButton
        onClick={() => handleViewClick(row._id)}
        aria-label="view"
        className={classes.viewBtn}
      >
        <Visibility style={{ fontSize: "19px" }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Éditer" arrow>
      <IconButton
        onClick={() => handleEditClick(row._id)}
        aria-label="edit"
        className={classes.editBtn}
      >
        <Edit style={{ fontSize: "19px" }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Supprimer" arrow>
      <IconButton
        onClick={() => handleDeleteClick(row._id)}
        aria-label="delete"
        className={classes.deleteBtn}
      >
        <Delete style={{ fontSize: "19px" }} />
      </IconButton>
    </Tooltip>
  </div>
</TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          component="div"
          count={investigationData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          classes={{
            root: classes.pagination,
          }}
        />
      </TableContainer>

      <DeleteModal
        open={open}
        handleClose={handleClose}
        handleDelete={() => handleDeleteInvestigation(selectedId)}
      />
    </>
  );
};

export default InvestigationTable;
